import React, { useState } from 'react';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd';
import { CustomButton } from '../../components';
import ReCAPTCHA from 'react-google-recaptcha';
import PublicHeader from '../../containers/PublicHeader';
import { Link } from 'react-router-dom-v5-compat';
import PdfViewer from '../../components/PdfViewer';
import { searchDocument } from '../../api/module/document_verification';

import cuvExample from '../../images/examples/cuv.png';

const { Text, Link: ExternalLink } = Typography;

const cuvFormat = /^[^-]{6,}-[^-]{4,}-[^-]{4,}-[^-]{4,}-[^-]{6,}$/;

const DocumentVerification = () => {
  const [documentData, setDocumentData] = useState();
  const [loading, setLoading] = useState(false);
  const [pdfURL, setPdfUrl] = useState();

  async function onFinish(data) {
    try {
      setLoading(true);
      setDocumentData();
      const { data: res } = await searchDocument(data);
      if (res.type === 3)
        Modal.info({
          title: 'Documento no disponible',
          content: (
            <>
              <Text>
                El CUV ingresado corresponde a un documento no disponible para
                verificación de terceros.
              </Text>
              <br />
              <Text>
                En caso de requerir su verificación, póngase en contacto con
                Corpoeducación al teléfono (+057) 601 3235550/49/51 o al correo
                electrónico corpoeducacion@corpoeducacion.org.co
              </Text>
            </>
          ),
        });
      else setDocumentData(res);
    } catch (err) {
      Modal.error({
        title: 'Documento no encontrado',
        content: (
          <>
            <Text>El CUV ingresado no fue encontrado.</Text>
            <br />
            <Text>
              Verifique el código digitado e intente nuevamente o póngase en
              contacto con Corpoeducación al teléfono (+057) 601 3235550/49/51 o
              al correo electrónico corpoeducacion@corpoeducacion.org.co
            </Text>
          </>
        ),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Row justify={'center'} align={'top'}>
      <Col xs={22} sm={19} md={16} lg={14} xl={12} className={'form-container'}>
        <PublicHeader />
        <Typography.Title className={'public-page-header'}>
          Verificación de documentos
        </Typography.Title>
        <ol>
          <li key="0">
            Ingrese el Código Único de Verificación - CUV, que se encuentra en
            la parte inferior del documento que recibió.
          </li>
          <li key="1">
            En caso de encontrar diferencias entre el documento que está
            verificando y la información que visualiza y desea reportarlo,
            comuníquelo al correo etica@corpoeducacion.org.co
          </li>
        </ol>
        <Form layout="vertical" scrollToFirstError onFinish={onFinish}>
          <Form.Item
            label="Código Único de Verificación - CUV"
            name="cuv"
            tooltip={{
              placement: 'top',
              // title: 'El formato del CUV',
              title: (
                <div style={{ padding: 5 }}>
                  El formato del CUV
                  <br />
                  Recuerde que debe tener 32 dígitos separados por 4 guiones.
                  <br />
                  <img
                    src={cuvExample}
                    alt="XXXXXX-XXXX-XXXX-XXXX-XXXXXX"
                    style={{ width: 600, height: 'auto', borderRadius: 4 }}
                  />
                </div>
              ),
              overlayInnerStyle: {
                width: 'max-content',
                maxWidth: 'none',
              },
            }}
            rules={[
              {
                required: true,
                message: 'Debe ingresar un CUV',
              },
              {
                pattern: cuvFormat,
                message:
                  'El formato del CUV no es el correcto. Por favor verifique el código digitado',
              },
            ]}
          >
            <Input placeholder="XXXXXX-XXXX-XXXX-XXXX-XXXXXX" allowClear />
          </Form.Item>
          <Form.Item
            name="agree"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Debe aceptar las políticas para continuar'),
                      ),
              },
            ]}
          >
            <Checkbox>
              <Text>
                He leído y acepto la{' '}
                <ExternalLink
                  href="https://corpoeducacion.org.co/nuestras-politicas/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política de tratamiento y protección de datos
                </ExternalLink>
              </Text>
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="recaptcha"
            rules={[
              {
                required: true,
                message: 'Realice la verificación de CAPTCHA',
              },
            ]}
          >
            <ReCAPTCHA
              sitekey={`${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`}
            />
          </Form.Item>
          <Form.Item className={'mb-0'}>
            <Space size={'small'}>
              <Link to={INTERNAL_LINKS.HOME}>
                <Button className={'back-button'} block htmlType="button">
                  Volver
                </Button>
              </Link>
              <CustomButton text="Buscar" loading={loading} block />
            </Space>
          </Form.Item>
        </Form>
        {!loading && documentData ? (
          documentData.type === 1 ? (
            <>
              <br />
              <Text strong>¡Documento encontrado!</Text>
              <br />
              <br />
              <CustomButton
                text="Visualizar"
                type="default"
                htmlType="button"
                onClick={() => setPdfUrl(documentData.url)}
              />
              <PdfViewer
                pdf={pdfURL}
                onCancel={() => setPdfUrl()}
                open={!!pdfURL}
              />
            </>
          ) : (
            <div
              style={{
                backgroundColor: '#f0f0f0',
                padding: '1em',
                border: '1px solid black',
                marginTop: '1em',
              }}
            >
              <Text strong>
                ¡Información encontrada CUV: {documentData.info.cuv}!
              </Text>
              <br />
              <br />
              <Text strong>
                CONTRATO DE PRESTACIÓN DE SERVICIOS No.{' '}
                {documentData.info.contrato_num}
              </Text>
              <br />
              <Text strong>ENTIDAD CONTRATANTE: </Text>
              <Text>{documentData.info.organizacion_nombre}</Text>
              <br />
              <Text strong>
                {documentData.info.organizacion_tipo_identificacion}:{' '}
              </Text>
              <Text>
                {documentData.info.organizacion_numero_identificacion}
              </Text>
              <br />
              <br />
              <Text strong>CONTRATISTA/PROVEEDOR: </Text>
              <Text>{documentData.info.contratista_nombre}</Text>
              <br />
              <Text strong>
                {documentData.info.contratista_tipo_identificacion}:{' '}
              </Text>
              <Text>{documentData.info.contratista_numero_identificacion}</Text>
              <br />
              <br />
              <Text strong>ESTADO: </Text>
              <Text>{documentData.info.estado}</Text>
              <br />
              <br />
              <Text strong>No. DE MODIFICACIONES: </Text>
              <Text>{documentData.info.numero_modificaciones}</Text>
              <br />
              <br />
              <Text strong>OBJETO: </Text>
              <Text>{documentData.info.objeto}</Text>
              <br />
              <br />
              <Text strong>OBLIGACIONES ESPECIALES DE EL/LA CONTRATISTA: </Text>
              {documentData.info.obligaciones.map((i) => (
                <>
                  <br />
                  <Text>{i}</Text>
                </>
              ))}
              <br />
              <br />
              <Text strong>DURACIÓN: </Text>
              {documentData.info.estado === 'En ejecución' ? (
                <Text>
                  Fecha de inicio el {documentData.info.fecha_inicio} y fecha de
                  finalización el {documentData.info.fecha_fin}, sin embargo, la
                  prestación del servicio estará vinculada al proyecto asignado
                  o centro de costos, pudiendo ser modificada entre las partes,
                  sin que por estos cambios se modifique el tipo de contrato.
                </Text>
              ) : (
                <Text>
                  Fecha de inicio el {documentData.info.fecha_inicio} y fecha de
                  finalización el {documentData.info.fecha_fin}.
                </Text>
              )}
              <br />
              <br />
              {documentData.info.estado === 'Terminado y liquidado' ? (
                <>
                  <Text strong>VALOR TOTAL PACTADO DEL CONTRATO: </Text>
                  {documentData.info.valor_total.map((line) => (
                    <>
                      <Text>{line}</Text>
                      <br />
                    </>
                  ))}
                  <br />
                  <Text strong>VALOR TOTAL EFECTIVO DEL CONTRATO: </Text>
                  <Text>{documentData.info.valor_efectivo}</Text>
                </>
              ) : (
                <>
                  <Text strong>VALOR TOTAL VIGENTE DEL CONTRATO: </Text>
                  {documentData.info.valor_total.map((line) => (
                    <>
                      <Text>{line}</Text>
                      <br />
                    </>
                  ))}
                </>
              )}
              <br />
              <br />
              <Text strong>** NOTA: </Text>
              <Text>
                Si se requiere obtener la información del clausulado comuníquese
                a través del correo corpoeducacion@corpoeducacion.org.co
              </Text>
            </div>
          )
        ) : null}
      </Col>
    </Row>
  );
};

export default DocumentVerification;
